import { DUNSSearchType } from '@/models/back-office-pages/duns'
import { FactoringProduct, FactoringProductType, MainProblem } from '@/models/back-office-pages/prospects'
import { RepresentativeDTO } from '@/models/common'
import { Direction } from '@/models/table'

export enum StopListType {
  COMPANY = 'COMPANY',
  PERSON = 'PERSON',
}

export enum StoplistStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export interface StoplistCompanyDTO {
  addedBy: string
  companyName: string
  createdOn: Date
  dunId: string
  factoringProduct: FactoringProductType
  id: string
  outstandingAmount: number
  limit: number
  problem: MainProblem
  registrationCode: string
  status: StoplistStatus
  updatedBy: string
  updatedOn: Date
  vatCode: string
  companyCountryCode: string
  canArchiveOrRestore: boolean
}

export interface Page_StoplistCompanyDTO {
  content: Array<StoplistCompanyDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface StopListCompaniesData {
  stoplistCompanies: Page_StoplistCompanyDTO
}

export interface StopListCompaniesVariables {
  country?: string
  dunId?: string
  companyName?: string
  registrationCode?: string
  pageNum?: number
  pageSize?: number
  sortDirection?: Direction
  sortField?: string
}

export interface StoplistCompanyDetailsDTO {
  companyDetails: StoplistCompanyDTO
  summary: string
  representatives: Array<RepresentativeDTO>
}

export interface AddStopListCompanyData {
  addStoplistCompany: StoplistCompanyDetailsDTO
}

export interface AddStopListCompanyFormValues {
  dunId: string
  companyCountryCode: string
  searchType: DUNSSearchType
  limit: number | string
  outstandingAmount: number | string
  problem: MainProblem | string
  factoringProduct: FactoringProductType | string
}

export interface AddStopListCompanyVariables {
  dunId: string
  limit: number
  outstandingAmount: number
  problem: MainProblem
  factoringProduct: FactoringProductType
}

export enum AddStopListCompanyFieldNames {
  PRODUCT = 'factoringProduct',
  PROBLEM = 'problem',
  LIMIT = 'limit',
  OUTSTANDING_AMOUNT = 'outstandingAmount',
}

export interface StopListCompanyDetailsData {
  stopListCompanyDetails: StoplistCompanyDetailsDTO
}

export interface StopListCompanyDetailsVariables {
  id: string
}

export interface UpdateStopListCompanyDescriptionData {
  updateStoplistCompanyDescription: StoplistCompanyDetailsDTO
}

export interface UpdateStopListCompanyDescriptionVariables {
  id: string
  summary: string
}

export enum UpdateStopListCompanyFieldNames {
  COUNTRY = 'companyCountryCode',
  REGISTRATION_CODE = 'registrationCode',
  DUN_ID = 'dunId',
  VAT = 'vatCode',
  PRODUCT = 'factoringProduct',
  PROBLEM = 'problem',
  LIMIT = 'limit',
  OUTSTANDING_AMOUNT = 'outstandingAmount',
  ADDED_BY = 'addedBy',
  ADDED_ON = 'createdOn',
  UPDATED_BY = 'updatedBy',
  UPDATED_ON = 'updatedOn',
}

export interface UpdateStopListCompanyDetailsData {
  updateStoplistCompany: StoplistCompanyDetailsDTO
}

export interface UpdateStopListCompanyDetailsVariables {
  dunId: string
  problem: MainProblem
  factoringProduct: FactoringProduct
  id: string
  limit: number
  outstandingAmount: number
}

export enum CompanyRepresentativeFieldNames {
  REPRESENTATIVES = 'representatives',
}

export interface UpdateStopListCompanyRepresentativesData {
  updateStoplistCompanyRepresentatives: StoplistCompanyDetailsDTO
}

export interface UpdateStopListCompanyRepresentativesVariables {
  id: string
  representatives: Array<RepresentativeDTO>
}

export interface StoplistPersonDTO {
  addedBy: string
  createdOn: Date | string
  birthYear: number
  birthDate: Date | string
  companyId: string
  companyName: string
  country: string
  dunId: string
  id: string
  name: string
  personalId: string
  registrationCode: string
  status: StoplistStatus
  stopListCompanyId: string
  surname: string
  updatedBy: string
  updatedOn: Date | string
  vatCode: string
  canArchiveOrRestore: boolean | string
}

export interface Page_StoplistPersonDTO {
  content: [StoplistPersonDTO]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface StopListPersonsData {
  stoplistPersons: Page_StoplistPersonDTO
}

export interface StopListPersonsVariables {
  dunId?: string
  country?: string
  sortDirection?: Direction
  surname?: string
  companyName?: string
  sortField?: string
  name?: string
  pageSize: number
  registrationCode?: string
  pageNum: number
}

export interface AddStopListPersonData {
  addStoplistPerson: StoplistPersonDTO
}

export interface AddStopListPersonVariables {
  country: string
  personalId: string
  birthYear: number | string
  surname: string
  name: string
  birthDate?: string
  birthType?: string
  createdOn?: string
  addedBy?: string
  updatedOn?: string
  updatedBy?: string
  status?: StoplistStatus
}

export interface StoplistPersonDetailsDTO {
  personDetails: StoplistPersonDTO
  summary: string
}

export interface StopListPersonDetailsData {
  stopListPersonDetails: StoplistPersonDetailsDTO
}

export interface StopListPersonDetailsVariables {
  id: string
}

export interface UpdateStopListPersonNotesData {
  updateStoplistPersonDescription: StoplistPersonDetailsDTO
}

export interface UpdateStopListPersonNotesVariables {
  id: string
  summary: string
}

export interface UpdateStopListPersonData {
  updateStoplistPerson: StoplistPersonDetailsDTO
}

export interface UpdateStopListPersonVariables {
  country: string
  personalId: string
  birthYear: number
  birthDate: Date
  surname: string
  name: string
  id: string
}

export enum BirthType {
  YEAR = 'year',
  DATE = 'date',
}

export enum StopListPersonFieldNames {
  COUNTRY = 'country',
  PERSONAL_ID = 'personalId',
  BIRTH_TYPE = 'birthType',
  BIRTH_YEAR = 'birthYear',
  BIRTH_DATE = 'birthDate',
  PLACE_OF_BIRTH = 'placeOfBirth',
  NAME = 'name',
  SURNAME = 'surname',
  ADDED_BY = 'addedBy',
  ADDED_ON = 'createdOn',
  UPDATED_BY = 'updatedBy',
  UPDATED_ON = 'updatedOn',
}

export interface StoplistPersonForm extends StoplistPersonDTO {
  birthType: BirthType | string
}

export enum StoplistPersonMatchType {
  DEBTOR_CONTACT_PERSON = 'DEBTOR_CONTACT_PERSON',
  KYC = 'KYC',
  PROSPECT_CONTACT_PERSON = 'PROSPECT_CONTACT_PERSON',
}

export interface StoplistPersonMatchDTO {
  companyName: string
  dealId: string
  id: string
  matchType: StoplistPersonMatchType
  name: string
  prospectId: string
  stoplistId: string
  stoplistStatus: StoplistStatus
  surname: string
}

export interface Page_StoplistPersonMatchDTO {
  content: Array<StoplistPersonMatchDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface StopListPersonsMatchesData {
  stoplistPersonsMatches: Page_StoplistPersonMatchDTO
}

export interface StopListPersonsMatchesVariables {
  sortDirection?: Direction
  sortField?: string
  pageSize: number
  id?: string
  pageNum: number
}

export enum StoplistCompanyMatchType {
  DEBTOR_COMPANY = 'DEBTOR_COMPANY',
  PROSPECT_COMPANY = 'PROSPECT_COMPANY',
  ANNUAL_REVIEW_COMPANY = 'ANNUAL_REVIEW_COMPANY',
  ANNUAL_REVIEW_DEBTOR_COMPANY = 'ANNUAL_REVIEW_DEBTOR_COMPANY',
  CLIENT_LIMIT_COMPANY = 'CLIENT_LIMIT_COMPANY',
}

export interface StoplistCompanyMatchDTO {
  companyName: string
  dealId: string
  id: string
  matchType: StoplistCompanyMatchType
  prospectId: string
  stoplistId: string
  stoplistStatus: StoplistStatus
}

export interface Page_StoplistCompanyMatchDTO {
  content: Array<StoplistCompanyMatchDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface StopListCompanyMatchesData {
  stoplistCompaniesMatches: Page_StoplistCompanyMatchDTO
}

export interface StopListCompanyMatchesVariables {
  sortDirection?: Direction
  sortField?: string
  pageSize: number
  id?: string
  pageNum: number
}
